import { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { GitMerge, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Find duplicate drivers (same name after normalisation) in the caller's
 * tenant and let the main user pick which record to keep. On merge, incident
 * / cost / trip / scorecard `driver_id`s are repointed and empty fields on
 * the "keep" row are back-filled from the duplicates.
 *
 * Server routes:
 *   GET  /api/admin/duplicate-drivers      → groups + ref counts
 *   POST /api/admin/merge-drivers          → { keep_id, delete_ids }
 */
export default function MergeDuplicateDriversDialog({ open, onOpenChange, onMerged }) {
  const [loading, setLoading] = useState(false);
  const [merging, setMerging] = useState(false);
  const [groups, setGroups] = useState([]);
  const [chosen, setChosen] = useState({}); // group_name -> keep_id

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/duplicate-drivers`);
      const g = r.data?.groups || [];
      setGroups(g);
      const initial = {};
      g.forEach((grp) => { initial[grp.name] = grp.suggested_keep_id; });
      setChosen(initial);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to scan for duplicates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) load(); }, [open]);

  const handleMerge = async (group) => {
    const keepId = chosen[group.name];
    const deleteIds = group.drivers.map((d) => d.id).filter((id) => id !== keepId);
    if (!keepId || deleteIds.length === 0) return;
    setMerging(true);
    try {
      const r = await axios.post(`${API_URL}/api/admin/merge-drivers`, {
        keep_id: keepId,
        delete_ids: deleteIds,
      });
      const refs = r.data?.refs_repointed || {};
      const total = Object.values(refs).reduce((a, b) => a + (b || 0), 0);
      toast.success(`Merged ${deleteIds.length} duplicate${deleteIds.length > 1 ? 's' : ''} — ${total} record${total !== 1 ? 's' : ''} relinked`);
      await load();
      onMerged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Merge failed');
    } finally {
      setMerging(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="merge-drivers-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitMerge className="w-5 h-5" />
            Merge Duplicate Drivers
          </DialogTitle>
          <DialogDescription>
            Drivers with the same name (case-insensitive) in your tenant. Pick which record to KEEP, and the others will be deleted with their incidents, costs, trips and scorecards relinked to the keeper.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-10 text-center text-slate-500">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
            Scanning drivers…
          </div>
        ) : groups.length === 0 ? (
          <div className="py-10 text-center text-slate-500" data-testid="no-duplicates">
            No duplicate driver names found ✓
          </div>
        ) : (
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            {groups.map((group) => (
              <div key={group.name} className="border border-slate-200 rounded-lg p-4" data-testid={`dup-group-${group.name}`}>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="font-semibold text-slate-800">{group.drivers[0]?.name || group.name}</div>
                    <div className="text-xs text-slate-500">{group.drivers.length} matching records — pick which one to KEEP</div>
                  </div>
                  <Button
                    size="sm"
                    className="bg-gray-700 hover:bg-gray-600 text-white"
                    disabled={merging || !chosen[group.name]}
                    onClick={() => handleMerge(group)}
                    data-testid={`merge-btn-${group.name}`}
                  >
                    {merging ? 'Merging…' : `Merge ${group.drivers.length - 1} into KEEP`}
                  </Button>
                </div>
                <div className="space-y-2">
                  {group.drivers.map((d) => {
                    const refs = d.ref_counts || {};
                    const totalRefs = Object.values(refs).reduce((a, b) => a + (b || 0), 0);
                    return (
                      <label
                        key={d.id}
                        className={`block border rounded-md p-3 cursor-pointer transition ${
                          chosen[group.name] === d.id ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:bg-slate-50'
                        }`}
                        data-testid={`dup-driver-${d.id}`}
                      >
                        <div className="flex items-start gap-3">
                          <input
                            type="radio"
                            name={`keep-${group.name}`}
                            value={d.id}
                            checked={chosen[group.name] === d.id}
                            onChange={() => setChosen({ ...chosen, [group.name]: d.id })}
                            className="mt-1"
                            data-testid={`radio-keep-${d.id}`}
                          />
                          <div className="flex-1 text-sm">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{d.name}</span>
                              {totalRefs > 0 && (
                                <Badge variant="secondary" className="text-xs">
                                  {totalRefs} linked record{totalRefs > 1 ? 's' : ''}
                                </Badge>
                              )}
                              <span className="text-xs text-slate-400 ml-auto">created {(d.created_at || '').slice(0, 10)}</span>
                            </div>
                            <div className="text-xs text-slate-500 mt-1 grid grid-cols-2 gap-x-4">
                              <div>ID #: {d.id_number || '—'}</div>
                              <div>License: {d.license_number || '—'}</div>
                              <div>Phone: {d.phone || '—'}</div>
                              <div>Email: {d.email || '—'}</div>
                              <div>Expiry: {d.license_expiry || '—'}</div>
                              <div>
                                Incidents {refs.incidents || 0} · Costs {refs.cost_entries || 0} · Trips {refs.trips || 0}
                              </div>
                            </div>
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="close-merge-dialog">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
